import Modal from './Modal';
import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  isOpen: boolean;
  adTitle?: string;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
}

export default function DeleteAdModal({ isOpen, adTitle, onClose, onConfirm }: Props): JSX.Element | null {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleDelete = async () => {
    try {
      setWorking(true);
      setError(null);
      await onConfirm();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete ad');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Listing">
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 text-red-800">
          <AlertTriangle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">This will permanently delete the ad.</p>
            {adTitle && (
              <p className="text-sm mt-1"><span className="text-gray-700">Ad:</span> {adTitle}</p>
            )}
            <p className="text-sm mt-1">This action cannot be undone.</p>
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleDelete}
            disabled={working}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
          >
            {working ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
